import {Component, Host, h, Element, Prop, State} from '@stencil/core';
import panzoom from "panzoom";

@Component({
  tag: 'z3-map-viewer',
  styleUrl: 'z3-map-viewer.css',
  shadow: true,
})
export class Z3MapViewer {

  /**
   * Own Properties
   */
  img!: HTMLImageElement
  container!: HTMLDivElement
  element!: HTMLDivElement
  panzoom!: any

  /**
   * Host Element
   */
  @Element() host: HTMLElement;

  /**
   * Props
   */
  @Prop() src: string = '';
  @Prop() maxZoom: number = 500;
  @Prop() mobileInitialLeft: number = 0;
  @Prop() mobileInitialTop: number = 100;

  /**
   * States
   */
  @State() loading: boolean = true

  /**
   * Component lifecycle events
   */
  connectedCallback() {}

  componentDidRender() {
    if (!this.panzoom && !this.loading) {
      this.initPanzoom()
    }
  }

  initPanzoom() {
    // Contain behavoir
    let initialZoom = Math.min((this.element.clientWidth / this.img.naturalWidth), (this.element.clientHeight / this.img.naturalHeight))
    let initialX = (this.container.offsetWidth - (this.img.width * initialZoom)) / 2
    let initialY = (this.container.offsetHeight - (this.img.height * initialZoom)) / 2
    // And pass it to panzoom
    this.panzoom = panzoom(this.element, {
      maxZoom: this.maxZoom/100,
      minZoom: 0.1,
      initialZoom: initialZoom,
      initialX:initialX,
      initialY: initialY
    })

    // Mobile init styles
    if( window.innerWidth < 768) {
      // Cover behavoir
      const containerWidth = this.element.clientWidth
      const containerHeight = this.element.clientHeight
      const imageWidth = this.img.naturalWidth
      const imageHeight = this.img.naturalHeight
      const scaleX = containerWidth / imageWidth
      const scaleY = containerHeight / imageHeight
      const scale = Math.max(scaleX, scaleY)
      const x = this.mobileInitialLeft + '%'
      const y = this.mobileInitialTop + '%'
      this.positionImage(x, y, scale)
    }
  }

  positionImage(x, y, scale) {
    // Examples of how to use positionImage:
    // positionImage(0, 0, scale)          // Top-left
    // positionImage('0%', '0%', scale)    // Same as above
    // positionImage('50%', '50%', scale)  // Center
    // positionImage(200, 100, scale)      // Specific pixel coordinates

    const containerWidth = this.element.clientWidth
    const containerHeight = this.element.clientHeight
    const imageWidth = this.img.naturalWidth * scale
    const imageHeight = this.img.naturalHeight * scale

    // Handle percentage values
    let xPos = x
    let yPos = y

    if (typeof x === 'string' && x.includes('%')) {
      xPos = (containerWidth - imageWidth) * (parseInt(x) / 100)
    }
    if (typeof y === 'string' && y.includes('%')) {
      yPos = (containerHeight - imageHeight) * (parseInt(y) / 100)
    }
    this.panzoom.moveTo(xPos, yPos)
    this.panzoom.zoomAbs(xPos, yPos, scale)
  }

  handleZoomIn() {
    let rect = this.container.getBoundingClientRect()
    let cx = rect.x + rect.width/2;
    let cy = rect.y + rect.height/2;
    let zoomBy = 2;
    this.panzoom.smoothZoom(cx, cy, zoomBy);
  }

  handleZoomOut() {
    let rect = this.container.getBoundingClientRect()
    let cx = rect.x + rect.width/2;
    let cy = rect.y + rect.height/2;
    let zoomBy = 0.5;
    this.panzoom.smoothZoom(cx, cy, zoomBy);
  }
  handleZoomReset() {
    const containerWidth = this.element.clientWidth
    const containerHeight = this.element.clientHeight
    const imageWidth = this.img.naturalWidth
    const imageHeight = this.img.naturalHeight
    const scaleX = containerWidth / imageWidth
    const scaleY = containerHeight / imageHeight
    const scale = Math.min(scaleX, scaleY)
    this.positionImage('50%', '50%', scale)
  }
  handleImageLoad() {
    this.loading = false
    /*
    const w = this.img.naturalWidth;
    const h = this.img.naturalHeight;
    let aspectRatio;
    if (w > h) {
      aspectRatio = w / h;
    } else {
      aspectRatio = h / w;
    }
    this.host.style.setProperty('aspect-ratio', aspectRatio.toString());
    */
  }

  render() {
    return (
      <Host>
        <div
          part="container"
          ref={(el) => this.container = el as HTMLDivElement}
             class={{
               'image-viewer__wrapper': true,
               'loading': this.loading,
             }}>
          <div
            class="image-viewer__element"
            ref={(el) => this.element = el as HTMLDivElement}>
            <img
              part="image"
              src={this.src}
              onLoad={() => this.handleImageLoad()}
              ref={(el) => this.img = el as HTMLImageElement}/>
          </div>
          <div class="loading-overlay"></div>
        </div>
        <div class="image-viewer__controls">
          <button
            part="zoom_in"
            onClick={() => this.handleZoomIn()}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
          <button
            part="zoom_out"
            onClick={() => this.handleZoomOut()}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14" />
            </svg>
          </button>
          <button
            part="zoom_reset"
            class="zoom-reset"
            onClick={() => this.handleZoomReset()}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="24" height="24" >
              <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 3.75H6A2.25 2.25 0 0 0 3.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0 1 20.25 6v1.5m0 9V18A2.25 2.25 0 0 1 18 20.25h-1.5m-9 0H6A2.25 2.25 0 0 1 3.75 18v-1.5" />
            </svg>
          </button>
        </div>
      </Host>
    );
  }

}
